import Error from '@/components/Error';
import Logo from '@/components/svg/Logo';
import settings from '@/constants/constants';
import { useDisplayChatForIc } from '@/hooks/useDisplayChatForIc';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import {
  VIDEO_CONSULTATION_TRIGGER_SEARCH_PARAM_KEY,
  useVideoConsultationTrigger,
} from '@/hooks/useVideoConsultationTrigger';
import Layout from '@/pcp/components/Layout';
import type { ErrorType } from '@montugroup/clinic-telecom';
import { PatientPortalPage, ConfigProvider as TelecomConfigProvider } from '@montugroup/clinic-telecom';
import type { TypedAnalyticsContexts } from '@montugroup/data-collection';
import { AnalyticsTrackingProvider, createFeatureFlagContext, featureFlagFilters } from '@montugroup/data-collection';
import { Box } from '@montugroup/design-system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FF_C4C_CHAT_NOTIFICATION_EXPERIMENT } from '../../constants/featureFlags';

const queryClient = new QueryClient();
const LogoElement = Logo({});

export const VideoConsultation = () => {
  const { key } = useParams<{ key: string }>();
  const { flags } = useFeatureFlags();
  const ldClient = useLDClient();
  const [searchParams] = useSearchParams();
  const { videoConsultationTrigger } = useVideoConsultationTrigger(
    searchParams.get(VIDEO_CONSULTATION_TRIGGER_SEARCH_PARAM_KEY),
  );
  const { displayChatForIc } = useDisplayChatForIc(videoConsultationTrigger);
  const displayAlternateChatNotification = flags[FF_C4C_CHAT_NOTIFICATION_EXPERIMENT];

  const flagContext = useMemo(
    () =>
      createFeatureFlagContext({
        ldClient,
        flags,
        filter: featureFlagFilters.includeOnly([FF_C4C_CHAT_NOTIFICATION_EXPERIMENT]),
      }),
    [ldClient, flags],
  );

  const updateTrackingContext = useCallback(
    (ctx: TypedAnalyticsContexts): TypedAnalyticsContexts => ({
      ...ctx,
      uiEvent: {
        ...ctx.uiEvent,
        context_id: 'video_consultation',
      },
      ...(flagContext.length > 0 && { flags: flagContext[0].data }),
    }),
    [flagContext],
  );

  return (
    <TelecomConfigProvider baseUrl={settings.baseUrl}>
      <QueryClientProvider client={queryClient}>
        <AnalyticsTrackingProvider update={updateTrackingContext}>
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
            {/* key will never be undefined here because the route is protected */}
            <PatientPortalPage
              renderErrorComponent={(type: ErrorType) => (
                <Layout contentMaxWidth="md">
                  <Error type={type} />
                </Layout>
              )}
              consultKey={key!}
              patientPortalUrl={settings.circuitLoginUrl}
              scheduleLaterUrl={settings.scheduleLaterUrl}
              consultationFeedbackUrl={settings.videoConsult.consultationFeedbackUrl}
              contactUsUrl={settings.videoConsult.contactUsUrl}
              privacyPolicyUrl={settings.videoConsult.privacyPolicyUrl}
              connectInstanceConfigUrl={settings.connect.instanceConfigUrl}
              logo={LogoElement}
              permissionPromptImageSrc={'/images/permissionPrompt.png'}
              sampleAudioSrc={'/assets/videoConsult-musicHoldShort.wav'}
              widgetId={settings.connect.chat.widgetId}
              snippetId={settings.connect.chat.snippetId}
              chatInterfaceClientUrl={settings.connect.chat.chatInterfaceClientUrl}
              displayAlternateChatNotification={displayAlternateChatNotification}
              displayChatForIc={displayChatForIc}
              videoConsultationTrigger={videoConsultationTrigger ?? undefined}
            />
          </Box>
        </AnalyticsTrackingProvider>
      </QueryClientProvider>
    </TelecomConfigProvider>
  );
};
